import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Users, Search, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { MyTruckRow } from "@/hooks/useMyTrucks";

interface CarrierLoadMatchesPaneProps {
  onBack: () => void;
  trucks: MyTruckRow[];
}

interface LoadMatch {
  id: string;
  origin: string;
  destination: string;
  equipment: string;
  pickupDate: string | null;
  rateAmount: number | null;
  totalMiles: number | null;
  matchedTruckId: string;
}

/**
 * Carrier-side AI Load Matching surface.
 * For each of the carrier's active posted trucks, finds platform loads
 * matching equipment + origin state + pickup window. Mirrors the matching
 * shape used in AIQuickActionsPanel (broker branch), inverted for carriers.
 */
export function CarrierLoadMatchesPane({ onBack, trucks }: CarrierLoadMatchesPaneProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<LoadMatch[]>([]);
  const [loading, setLoading] = useState(true);

  const activeTrucks = trucks.filter(t => t.status === "active");
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user) { setLoading(false); return; }
      setLoading(true);
      setUsedFallback(false);

      try {
        const today = new Date().toISOString().split("T")[0];
        const { data: loads } = await supabase
          .from("loads")
          .select("id, origin_city, origin_state, destination_city, destination_state, equipment_type, pickup_date, rate_amount, total_miles, status, created_by")
          .eq("status", "posted")
          .gte("pickup_date", today)
          .order("pickup_date", { ascending: true })
          .limit(300);

        if (cancelled) return;
        const all = (loads || []).filter(l => l.created_by !== user.id);

        // No active trucks → show the freshest 18 loads as a recent board preview.
        if (activeTrucks.length === 0) {
          if (!cancelled) {
            setUsedFallback(true);
            setMatches(all.slice(0, 18).map(load => ({
              id: load.id,
              origin: [load.origin_city, load.origin_state].filter(Boolean).join(", "),
              destination: [load.destination_city, load.destination_state].filter(Boolean).join(", "),
              equipment: load.equipment_type || "—",
              pickupDate: load.pickup_date,
              rateAmount: load.rate_amount,
              totalMiles: load.total_miles,
              matchedTruckId: "",
            })));
            setLoading(false);
          }
          return;
        }

        // Tier 1: strict — equipment + origin state match
        // Tier 2: loose — equipment match only
        // Tier 3: fallback — any state with pickup in next 14 days
        const truckMeta = activeTrucks.map(t => {
          const eqNorm = (t.equipmentType || "").toLowerCase().replace(/[_\s-]/g, "");
          const stMatch = (t.origin || "").match(/,\s*([A-Z]{2})\b/) || (t.origin || "").match(/\b([A-Z]{2})\b/);
          return { id: t.id, eqNorm, originState: stMatch ? stMatch[1] : null };
        });

        const seen = new Set<string>();
        const tierA: LoadMatch[] = [];
        const tierB: LoadMatch[] = [];

        for (const load of all) {
          if (seen.has(load.id)) continue;
          const loadEqNorm = (load.equipment_type || "").toLowerCase().replace(/[_\s-]/g, "");

          let bestTier: 0 | 1 | 2 = 0;
          let bestTruckId = "";
          for (const tm of truckMeta) {
            const eqOk = tm.eqNorm && loadEqNorm ? tm.eqNorm === loadEqNorm : true;
            const stateOk = tm.originState && load.origin_state ? tm.originState === load.origin_state : false;
            if (eqOk && stateOk) { bestTier = 1; bestTruckId = tm.id; break; }
            if (eqOk && bestTier === 0) { bestTier = 2; bestTruckId = tm.id; }
          }
          if (bestTier === 0) continue;

          const item: LoadMatch = {
            id: load.id,
            origin: [load.origin_city, load.origin_state].filter(Boolean).join(", "),
            destination: [load.destination_city, load.destination_state].filter(Boolean).join(", "),
            equipment: load.equipment_type || "—",
            pickupDate: load.pickup_date,
            rateAmount: load.rate_amount,
            totalMiles: load.total_miles,
            matchedTruckId: bestTruckId,
          };
          seen.add(load.id);
          if (bestTier === 1) tierA.push(item);
          else tierB.push(item);
        }

        const combined = [...tierA, ...tierB].slice(0, 18);
        if (!cancelled) setMatches(combined);
      } catch (err) {
        console.error("CarrierLoadMatchesPane error:", err);
        if (!cancelled) setMatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user, JSON.stringify(activeTrucks.map(t => t.id))]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button onClick={onBack} className="p-1 rounded hover:bg-muted transition-colors">
          <ArrowLeft className="h-4 w-4 text-muted-foreground" />
        </button>
        <Users className="h-4 w-4 text-success" />
        <h4 className="text-caption font-bold text-foreground">AI Load Matching</h4>
        {matches.length > 0 && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-success/15 text-success">
            {matches.length} {usedFallback ? "live load" : "match"}{matches.length !== 1 ? (usedFallback ? "s" : "es") : ""}
          </span>
        )}
      </div>

      {usedFallback && matches.length > 0 && (
        <div className="px-3 py-2 border-b bg-warning/5 flex items-center gap-2">
          <Truck className="h-3.5 w-3.5 text-warning flex-shrink-0" />
          <p className="text-[11px] text-foreground/80 leading-snug flex-1">
            You don't have any active trucks posted yet. Showing the freshest live loads on the platform — post a truck to get personalized lane matches.
          </p>
          <Button size="sm" className="h-6 text-[10px] px-2.5 flex-shrink-0" onClick={() => navigate("/post-truck")}>
            Post a truck
          </Button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Matching loads against your posted trucks…</span>
          </div>
        ) : matches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
            <Search className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {activeTrucks.length === 0
                ? "No live loads on the platform right now."
                : "No live load matches yet."}
            </p>
            <p className="text-xs text-muted-foreground/70">
              {activeTrucks.length === 0
                ? "Check back shortly — new loads post throughout the day."
                : `We'll keep watching loads on your active lanes (${activeTrucks.length} truck${activeTrucks.length !== 1 ? "s" : ""}).`}
            </p>
            <div className="flex gap-2 mt-2">
              {activeTrucks.length === 0 && (
                <Button size="sm" onClick={() => navigate("/post-truck")}>
                  Post a truck
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => navigate("/search")}>
                Browse all loads
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[1fr_80px_80px_70px_80px] items-center px-3 py-1 border-b border-border/50">
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Lane</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Equipment</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Pickup</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">RPM</span>
              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Action</span>
            </div>
            {matches.map((m, i) => {
              const rpm = m.rateAmount && m.totalMiles && m.totalMiles > 0 ? m.rateAmount / m.totalMiles : null;
              return (
                <div
                  key={m.id}
                  className={`grid grid-cols-[1fr_80px_80px_70px_80px] items-center px-3 py-2 border-b border-border/30 hover:bg-muted/30 transition-colors ${i % 2 === 1 ? "bg-muted/20" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                      <span className="truncate">{m.origin || "—"}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="truncate">{m.destination || "—"}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {m.totalMiles ? `${m.totalMiles.toLocaleString()} mi` : "— mi"}
                      {m.rateAmount ? ` · $${m.rateAmount.toLocaleString()}` : " · Call"}
                    </div>
                  </div>
                  <span className="text-[10px] font-medium text-foreground/70 text-right truncate capitalize">
                    {m.equipment}
                  </span>
                  <span className="text-[10px] font-medium text-muted-foreground text-right tabular-nums">
                    {m.pickupDate || "—"}
                  </span>
                  <span className="text-[10px] font-semibold text-primary tabular-nums text-right">
                    {rpm ? `$${rpm.toFixed(2)}` : "—"}
                  </span>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2.5 flex-shrink-0"
                      onClick={() => navigate(`/search?loadId=${m.id}`)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
